import React from 'react'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Card variant style
   */
  variant?: 'default' | 'elevated' | 'outlined' | 'filled'
  /**
   * Card size
   */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Hover effect
   */
  hover?: boolean
  /**
   * Clickable card
   */
  clickable?: boolean
  /**
   * Loading state
   */
  loading?: boolean
  /**
   * Card header
   */
  header?: React.ReactNode
  /**
   * Card footer
   */
  footer?: React.ReactNode
  /**
   * Card content
   */
  children: React.ReactNode
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      size = 'md',
      hover = false,
      clickable = false,
      loading = false,
      header,
      footer,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'rounded-lg',
      'transition-all',
      'duration-200',
      'overflow-hidden'
    ]

    const variantClasses = {
      default: [
        'bg-white',
        'border',
        'border-gray-200',
        'shadow-sm'
      ],
      elevated: [
        'bg-white',
        'shadow-md',
        'hover:shadow-lg'
      ],
      outlined: [
        'bg-white',
        'border-2',
        'border-gray-300'
      ],
      filled: [
        'bg-gray-50',
        'border',
        'border-gray-200'
      ]
    }

    const sizeClasses = {
      sm: ['p-4'],
      md: ['p-6'],
      lg: ['p-8']
    }

    const interactiveClasses = [
      clickable && [
        'cursor-pointer',
        'hover:shadow-md',
        'active:scale-[0.98]'
      ],
      hover && [
        'hover:shadow-md',
        'hover:-translate-y-1'
      ]
    ].filter(Boolean).flat()

    const loadingClasses = loading ? ['animate-pulse'] : []

    const cardClasses = [
      ...baseClasses,
      ...variantClasses[variant],
      ...sizeClasses[size],
      ...interactiveClasses,
      ...loadingClasses,
      className
    ].join(' ')

    return (
      <div
        ref={ref}
        className={cardClasses}
        {...props}
      >
        {header && (
          <div className="border-b border-gray-200 pb-4 mb-4">
            {header}
          </div>
        )}
        
        <div className={loading ? 'opacity-50' : ''}>
          {children}
        </div>
        
        {footer && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            {footer}
          </div>
        )}
      </div>
    )
  }
)

Card.displayName = 'Card'

export { Card }
